import { Link, useRouterState } from '@tanstack/react-router';
import { useVendorAuthStore } from '../store/vendorAuth.ts';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

type NavItem = { to: string; label: string; icon: string; matchPrefix?: string };

// Material 3 mobile navigation bar — the same destinations as the desktop
// drawer, with the active destination marked by a tonal pill behind a filled
// icon. Sign-out lives in the mobile top app bar (see VendorLayout), matching
// the staff shell.
export function VendorBottomNav(): JSX.Element | null {
  const vendor = useVendorAuthStore((s) => s.vendor);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!vendor) return null;

  const items: NavItem[] = [
    { to: '/vendor', label: t.vendorPortal.navQuotations, icon: 'description' },
    { to: '/vendor/open', label: t.vendorPortal.navOpen, icon: 'inbox', matchPrefix: '/vendor/open' },
    { to: '/vendor/awards', label: t.vendorPortal.navAwards, icon: 'workspace_premium', matchPrefix: '/vendor/awards' },
  ];

  return (
    <nav
      aria-label={t.nav.mainNav}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[hsl(var(--chrome))]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex items-stretch">
        {items.map((item) => {
          const active = item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className="flex h-14 w-full flex-col items-center justify-center gap-1 active:scale-95 transition-transform"
              >
                <span className="relative inline-flex items-center justify-center">
                  <span
                    className={[
                      'inline-flex items-center justify-center rounded-full px-4 py-0.5 transition-colors',
                      active
                        ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                        : 'text-muted-foreground',
                    ].join(' ')}
                  >
                    <Icon name={item.icon} size={22} fill={active} />
                  </span>
                </span>
                {/* No `tracking-tight`: negative letter-spacing distorts Thaana nav labels. */}
                <span
                  className={
                    active
                      ? 'text-[11px] leading-none text-ink font-medium'
                      : 'text-[10.5px] leading-none text-muted-foreground'
                  }
                >
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
